/**
 * Runtime Typography CSS Generator
 *
 * This module loads the content.json file at runtime and generates equivalent CSS
 * for the typography styles that were previously generated at build time.
 */

interface FontData {
    family: string;
    "sizes-lms": number[];
    "line-height-lms": number[];
}

interface FontConfig {
    [fontKey: string]: FontData;
}

interface ColorConfig {
    // definition for which colors to expect in scss
    "white": string;
    "icon-dark": string;
    "icon-light": string;
    "cd-primary": string;
    "cd-secondary": string;
    "cd-text": string;
    "cd-text-inverted": string;
    "btn": string;
    "btn-hover": string;
    "tooltip-text": string;
    "percent90": string;
    "percent50": string;
    "percent10": string;
    "region-west": string;
    "region-east": string;
    "cohort-candle-w": string;
    "cohort-candle-m": string;
    "cohort-tooltip-bg": string;
}

interface FontAsset {
    url: string;
    familyName: string;
    format: string; // could be a union type like "woff" | "woff2" | "ttf"
}

type FontAssetsConfig = FontAsset[]


interface CommonData {

    fonts: FontConfig;
    colors: ColorConfig;

    [key: string]: any;
}

/**
 * Generates CSS for font-family classes (ex: font_family_title)
 * @param {FontConfig} fontConfig - The font configuration from content.json
 * @returns {string} - The generated CSS
 */
function generateFontFamilyClasses(fontConfig: FontConfig): string {
    const classes: string[] = [];

    Object.entries(fontConfig).forEach(([fontKey, fontData]) => {
        // Generate class for @mixin font-family-{fontKey}
        classes.push(`.font_family_${fontKey} {
  font-family: ${fontData.family};
}`);
    });

    return classes.join('\n\n');
}

/**
 * Generates CSS for font size classes (ex: font-text-s)
 * @param {FontConfig} fontConfig - The font configuration from content.json
 * @returns {string} - The generated CSS
 */
function generateFontSizeClasses(fontConfig: FontConfig): string {
    const sizeLabels: string[] = ['l', 'm', 's'];
    const classes: string[] = [];

    /* --- responsive "general" typo classes, example:
    .font_title {
        font-family: 'Söhne Kräftig', sans-serif;
        font-size: 28px;
        line-height: 36px;
      }

    @media (max-width: 750px) {
      .font_title {
          font-size: 24px;
          line-height: 30px;
        }
      }

    @media (max-width: 550px) {
      .font_title {
          font-size: 20px;
          line-height: 26px;
        }
      }
    */
    Object.entries(fontConfig).forEach(([fontKey, fontData]) => {
        classes.push(`.font_${fontKey} {
  font-family: ${fontData.family};
  font-size: ${fontData["sizes-lms"][0]}px;
  line-height: ${fontData["line-height-lms"][0]}px;
}\n
@media (max-width: 750px) {
    .font_${fontKey} {
      font-size: ${fontData["sizes-lms"][1]}px;
      line-height: ${fontData["line-height-lms"][1]}px;
    }
}\n
@media (max-width: 550px) {
    .font_${fontKey} {
      font-size: ${fontData["sizes-lms"][2]}px;
      line-height: ${fontData["line-height-lms"][2]}px;
    }
}`);
    });

    /* --- explicit typo size classes, example:
    .font_title-s {
        font-family: 'Söhne Kräftig', sans-serif;
        font-size: 20px;
        line-height: 26px;
    }
    */
    Object.entries(fontConfig).forEach(([fontKey, fontData]) => {
        sizeLabels.forEach((label, index) => {
            const fontSize = fontData["sizes-lms"] ? `font-size: ${fontData["sizes-lms"][index]}px;` : "";
            const lineHeight = fontData["line-height-lms"] ? `line-height: ${fontData["line-height-lms"][index]}px;` : "";

            classes.push(`.font_${fontKey}_${label} {
  font-family: ${fontData.family};
  ${fontSize}
  ${lineHeight}
}`);
        });
    });

    return classes.join('\n\n');
}

/**
 * Generates CSS for typography classes
 * @returns {string} - The generated CSS
 */
function generateTypoClasses(fontConfig: FontConfig): string {
    return `
/* Typography classes */
:root {
  --root-line-height: ${fontConfig.text["line-height-lms"][0]}px;
}

html, body {
  color: #161616;   // TODO move to generated color functions 
  font-weight: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-family: ${fontConfig.text.family};
  font-size: ${fontConfig.text["sizes-lms"][0]}px;
  line-height: ${fontConfig.text["line-height-lms"][0]}px;
}

h1 {
  font-family: ${fontConfig.title.family};
  font-size: ${fontConfig.title["sizes-lms"][0]}px;
  line-height: ${fontConfig.title["line-height-lms"][0]}px;
}

@media (max-width: 750px) {
  h1 {
    font-size: ${fontConfig.title["sizes-lms"][1]}px;
    line-height: ${fontConfig.title["line-height-lms"][1]}px;
  }
}

@media (max-width: 550px) {
  h1 {
    font-size: ${fontConfig.title["sizes-lms"][2]}px;
    line-height: ${fontConfig.title["line-height-lms"][2]}px;
  }
}

h2, h3, h4, h5, h6 {
  font-family: ${fontConfig.head.family};
  font-size: ${fontConfig.head["sizes-lms"][0]}px;
  line-height: ${fontConfig.head["line-height-lms"][0]}px;
}

@media (max-width: 750px) {
  h2, h3, h4, h5, h6 {
    font-size: ${fontConfig.head["sizes-lms"][1]}px;
    line-height: ${fontConfig.head["line-height-lms"][1]}px;
  }
}

@media (max-width: 550px) {
  h2, h3, h4, h5, h6 {
    font-size: ${fontConfig.head["sizes-lms"][2]}px;
    line-height: ${fontConfig.head["line-height-lms"][2]}px;
  }
}

.typo_text_bold_xl {
  font-family: 'Söhne Buch', sans-serif;
  font-size: 2.25em;
  font-weight: bold;
}

.typo_bold_18 {
  font-family: 'Söhne Kräftig', sans-serif;
  font-size: 1.125em;
}

.typo_bold_16 {
  font-family: 'Söhne Buch', sans-serif;
  font-size: 1em;
}

.typo_regular_16 {
  font-family: 'Söhne Buch', sans-serif;
  font-size: 1em;
}`;
}

function generateFontEmbeddings(fontAssets: FontAssetsConfig) {
    let fontCSS = ''
    fontAssets.forEach(element => {
        fontCSS += `
      @font-face {
        font-family: '${element.familyName}';
        src: url('${element.url}') format('${element.format}');
        font-display: swap;
      }
    `
    })

    return fontCSS
}

/**
 * Generates the complete runtime typography CSS
 * @param {CommonData} commonData - The parsed content JSON
 * @returns {string} - The complete CSS
 */
function generateTypoCSS(commonData: CommonData): string {
    const fontConfig = commonData.fonts;
    const fontAssets: FontAssetsConfig = commonData.fontAssets;

    return [
        '/* Generated Runtime Typography CSS */',
        '/* Font Families */',
        generateFontEmbeddings(fontAssets),
        '/* Font Family Classes */',
        generateFontFamilyClasses(fontConfig),
        '/* Font Size Classes */',
        generateFontSizeClasses(fontConfig),
        '/* Typography Classes */',
        generateTypoClasses(fontConfig)
    ].join('\n\n');
}

function generateColorCSS(commonData: CommonData) {
    const colorConfig = commonData.colors;

    const css = [
        '/* Generated Runtime Color CSS */',
        ':root {',
        Object.entries(colorConfig).map(([cssVariable, cssColor]) => `  --${cssVariable}: ${cssColor};`).join('\n'),
        '}'
    ].join('\n');

    return css
}

/**
 * Injects the generated CSS into the document
 * @param {string} css - The CSS to inject
 */
function injectRuntimeCSS(css: string, id: string): void {
    const styleElement = document.createElement('style');
    styleElement.id = `${id}-${Date.now()}`;
    styleElement.textContent = css;
    document.head.appendChild(styleElement);
}

export function generateThemeCSS(commonData: CommonData) {
    injectRuntimeCSS(generateTypoCSS(commonData), 'runtime-typo');
    injectRuntimeCSS(generateColorCSS(commonData), 'runtime-color');
}
